import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../api';

export default function AdminBanners() {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadBanners();
    }, []);

    const loadBanners = async () => {
        try {
            const data = await api.getAllBannersAdmin();
            setBanners(data);
        } catch (error) {
            console.error('Error loading banners:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (banner) => {
        try {
            await api.updateBanner(banner.id, { is_active: !banner.is_active });
            loadBanners();
        } catch (error) {
            alert('Error updating banner: ' + error.message);
        }
    };

    const handleDelete = async (bannerId) => {
        if (!window.confirm('Are you sure you want to delete this banner?')) return;

        try {
            await api.deleteBanner(bannerId);
            setBanners(banners.filter(b => b.id !== bannerId));
        } catch (error) {
            alert('Error deleting banner: ' + error.message);
        }
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                <div>
                    <h1 className="admin-page-title" style={{ marginBottom: '8px' }}>Banner Management</h1>
                    <p style={{ color: 'var(--v2-text-muted)' }}>
                        Manage homepage promotional banners
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <Link 
                        to="/admin/banners/create" 
                        style={{ 
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '10px 20px', borderRadius: '12px',
                            background: 'var(--v2-primary)', border: 'none',
                            color: 'white', fontWeight: '600', fontSize: '14px',
                            cursor: 'pointer', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                            textDecoration: 'none'
                        }}
                    >
                        <Plus size={18} /> Add Banner
                    </Link>
                </div>
            </div>

            {/* Stats */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px',
                marginBottom: '32px'
            }}>
                <div className="card" style={{ padding: '24px' }}>
                    <div style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>Total Banners</div>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--primary)' }}>
                        {banners.length}
                    </div>
                </div>
                <div className="card" style={{ padding: '24px' }}>
                    <div style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>Active</div>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--success)' }}>
                        {banners.filter(b => b.is_active).length}
                    </div>
                </div>
            </div>

            {/* Banners Table */}
            {loading ? (
                <div className="admin-card-premium" style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: '40px', height: '40px', border: '3px solid var(--v2-border)', borderTopColor: 'var(--v2-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                </div>
            ) : (
                <div className="admin-table-container">
                    <table className="admin-table-premium">
                        <thead>
                            <tr>
                                <th>Banner Preview</th>
                                <th>Display Details</th>
                                <th>Display Order</th>
                                <th>Visibility</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {banners.map((banner) => (
                                <tr key={banner.id}>
                                    <td>
                                        <img
                                            src={banner.desktop_image_url || 'https://via.placeholder.com/100x50'}
                                            alt={banner.title}
                                            style={{
                                                width: '120px',
                                                height: '60px',
                                                borderRadius: '12px',
                                                objectFit: 'cover',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--v2-text-main)' }}>{banner.title}</div>
                                        {banner.link && (
                                            <div style={{ fontSize: '12px', color: 'var(--v2-text-muted)', marginTop: '4px' }}>
                                                🔗 {banner.link.length > 40 ? `${banner.link.substring(0, 40)}...` : banner.link}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <span className="status-tag role" style={{ fontWeight: '800', background: 'var(--v2-border-light)' }}>
                                            Pos {banner.order}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            onClick={() => handleToggleActive(banner)}
                                            className={`status-tag ${banner.is_active ? 'active' : 'suspended'}`}
                                            style={{ cursor: 'pointer', border: 'none' }}
                                        >
                                            {banner.is_active ? (
                                                <><Eye size={12} style={{ marginRight: '6px' }} /> Visible</>
                                            ) : (
                                                <><EyeOff size={12} style={{ marginRight: '6px' }} /> Hidden</>
                                            )}
                                        </button>
                                    </td>
                                    <td>
                                        <div className="table-actions">
                                            <Link
                                                to={`/admin/banners/edit/${banner.id}`}
                                                className="table-action-btn edit"
                                                title="Edit Banner"
                                            >
                                                <Edit2 size={18} />
                                            </Link>
                                            <button
                                                onClick={() => handleDelete(banner.id)}
                                                className="table-action-btn delete"
                                                title="Delete Banner"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
